import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { DataViewOption } from "@/components/cards/widgets/TableFilter";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { useTagsDataContext } from "@/providers/TagsDataProvider";
import { usePageStructure } from "@/providers/PageStructureProvider";
import { useNewItemsNotification } from "@/hooks/useNewItemsNotification";

/**
 * TODO: DataSource Migration
 * 
 * This hook currently uses multiple providers for data fetching.
 * Future refactoring should migrate to use the unified useDataSource hook:
 * 
 * ```typescript
 * import { useDataSource } from "@/hooks/useDataSource";
 * 
 * // Replace provider calls with:
 * const { rawData, cards, tableData, fieldMetadata } = useDataSource('objects', { 
 *   folderId: selectedFolderId 
 * });
 * ```
 * 
 * This will simplify the data flow and ensure all transformations 
 * go through the DataSource layer.
 * 
 * See: platform/src/hooks/useDataSource.ts
 */

// Import all data providers
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { useGlossaryBlueprintsDataContext } from "@/providers/GlossaryBlueprintsDataProvider";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { useDialogDataContext } from "@/providers/DialogDataProvider";
import { useTypesDataContext } from "@/providers/TypesDataProvider";

// Import logic flows store
import { logicFlowStore, fetchLogicFlows, getCachedLogicFlows } from "@/lib/stores/logicFlowStore";

import { GlossaryBlueprint } from "@/types-games";
import { GlossaryItem } from "@/lib/stores/objectsStore";
import { PageLayoutConfig } from "@/components/layout/index";
import { serializePageConfig } from "@/lib/utils/pageConfigLoader";
import { ItemTypeConfig, ItemType } from "@/pages/unified-items/configs/types";

export interface UseBaseItemsPageProps {
  itemType: ItemType;
  config: ItemTypeConfig;
}

export interface UseBaseItemsPageReturn {
  // Data
  data: any[];
  isLoading: boolean;
  cards: CardData[];
  selectedGame: any;
  tags: any[];
  glossaryBlueprints: GlossaryBlueprint[];

  // Data view mode support
  availableSections: DataViewOption[];
  availableFields: DataViewOption[];
  templateLookup: Record<string, { id: string; name: string; color?: string }>;
  rawItems: any[];

  // State
  isEditModalOpen: boolean;
  isAddWizardOpen: boolean;
  isCreateWizardOpen: boolean;
  selectedBlueprint: GlossaryBlueprint | null;
  selectedGlossaryItem: GlossaryItem | null;
  isEditMode: boolean;

  // Setters
  setIsEditModalOpen: (value: boolean) => void;
  setIsAddWizardOpen: (value: boolean) => void;
  setIsCreateWizardOpen: (value: boolean) => void;
  setSelectedBlueprint: (value: GlossaryBlueprint | null) => void;
  setSelectedGlossaryItem: (value: GlossaryItem | null) => void;
  setIsEditMode: (value: boolean) => void;
  setUpdateTrigger: React.Dispatch<React.SetStateAction<number>>;

  // Config
  pageLayoutConfig: PageLayoutConfig;
  setPageLayoutConfig: React.Dispatch<React.SetStateAction<PageLayoutConfig>>;
  config: ItemTypeConfig;

  // Providers
  toast: ReturnType<typeof useToast>["toast"];
  confirmation: ReturnType<typeof useConfirmation>["confirmation"];
  navigate: ReturnType<typeof useNavigate>;
  getUserNameById: (id: string) => string;
  currentPageStructure: any;
  updateSection: (id: string, section: string, config: any) => Promise<void>;

  // Data provider functions
  deleteDocument: (id: string, gameId: string) => Promise<void>;
  createDocument: (data: any) => Promise<any>;
  deleteDialogItem: (id: string, gameId: string) => Promise<void>;
  createDialogItem: (data: any) => Promise<any>;
  updateDialogItem: (data: any) => Promise<any>;
  
  // Refresh function
  refreshData: () => Promise<void>;
  
  // Real-time sync
  newItemCount: number;
  clearNewItems: () => void;
}

export function useBaseItemsPage({
  itemType,
  config,
}: UseBaseItemsPageProps): UseBaseItemsPageReturn {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const { selectedGame, getUserNameById } = useSimplifiedUserData();
  const { currentPageStructure, updateSection } = usePageStructure();
  const { tags, isLoading: tagsLoading } = useTagsDataContext();
  const navigate = useNavigate();
  const { isEditMode, setIsEditMode } = useOutletContext<{
    isEditMode: boolean;
    setIsEditMode: (mode: boolean) => void;
  }>();

  // Get the appropriate data provider
  const { glossaryItems: allGlossaryItems, isLoading: glossaryLoading, loadData: loadGlossaryData } =
    useGlossaryDataContext();
  const { glossaryBlueprints, isLoading: blueprintsLoading, loadData: loadBlueprintsData } =
    useGlossaryBlueprintsDataContext();
  const {
    documents,
    isLoading: documentsLoading,
    deleteDocument,
    createDocument,
    loadData: loadDocumentsData,
  } = useDocumentDataContext();
  const {
    dialogs,
    isLoading: dialogsLoading,
    deleteDialogItem,
    createDialogItem,
    updateDialogItem,
    loadData: loadDialogsData,
  } = useDialogDataContext();
  const { types, isLoading: typesLoading, refreshTypes } = useTypesDataContext();

  // For logic flows, use store directly with subscription
  const [flows, setFlows] = useState<any[]>([]);
  const [flowsLoading, setFlowsLoading] = useState(false);

  useEffect(() => {
    if (itemType === "flows" && selectedGame?.id) {
      // Fetch flows and subscribe to store updates
      console.log('[useBaseItemsPage] Fetching flows for game:', selectedGame.id);
      setFlowsLoading(true);
      fetchLogicFlows(selectedGame.id, toast)
        .then(() => {
          // Initial load after fetch
          const cachedFlows = getCachedLogicFlows(selectedGame.id);
          console.log('[useBaseItemsPage] Fetched flows:', cachedFlows.length, cachedFlows);
          setFlows(cachedFlows);
        })
        .finally(() => {
          setFlowsLoading(false);
        });

      // Subscribe to store updates
      const unsubscribe = logicFlowStore.subscribe(() => {
        const cachedFlows = getCachedLogicFlows(selectedGame.id);
        console.log('[useBaseItemsPage] Flows updated from store:', cachedFlows.length);
        setFlows(cachedFlows);
      });

      return unsubscribe;
    }
  }, [itemType, selectedGame?.id]); // Removed toast from dependencies

  // Map itemType to the notification system's ItemType
  const getNotificationItemType = (): 'object' | 'dialog' | 'document' | 'template' | 'flow' | null => {
    switch (itemType) {
      case "library":
        return "object";
      case "dialogs":
        return "dialog";
      case "documents":
        return "document";
      case "blueprints":
        return "template";
      case "flows":
        return "flow";
      case "variables":
        return null; // Variables don't use the update system yet
      default:
        return null;
    }
  };

  // Set up real-time notifications for new items
  const notificationItemType = getNotificationItemType();
  const { newItemCount, newItemIds, clearNewItems } = useNewItemsNotification({
    itemType: notificationItemType || 'object', // Provide fallback
    gameId: selectedGame?.id,
    enabled: !!selectedGame?.id && !!notificationItemType,
    onNewItem: (itemId, itemType, createdBy) => {
      console.log(`[useBaseItemsPage] New ${itemType} created by ${createdBy}:`, itemId);
      
      // Show toast notification
      toast({
        title: "New item created",
        description: `${createdBy} created a new ${config.title.toLowerCase().replace(/s$/, '')}`,
        action: {
          label: "Refresh",
          onClick: () => {
            refreshData();
            clearNewItems();
          },
        },
      });
    },
  });

  // Filter glossary items based on item type
  const glossaryItems = useMemo(() => {
    if (itemType === "library") {
      return allGlossaryItems.filter(item => 
        item.type === "glossary" || item.type === "Library Object" || item.type === "document"
      );
    } else {
      return allGlossaryItems.filter(item => item.type === "glossary" || item.type === "Library Object");
    }
  }, [allGlossaryItems, itemType]);

  // Determine which data to use based on item type
  const getData = () => {
    switch (itemType) {
      case "blueprints":
        return glossaryBlueprints || [];
      case "documents":
        return documents || [];
      case "library":
        return glossaryItems || [];
      case "dialogs":
        return dialogs || [];
      case "flows":
        return flows || [];
      case "variables":
        return types || [];
      default:
        return [];
    }
  };

  const getLoading = () => {
    switch (itemType) {
      case "blueprints":
        return blueprintsLoading || tagsLoading;
      case "documents":
        return documentsLoading || tagsLoading;
      case "library":
        return glossaryLoading || tagsLoading;
      case "dialogs":
        return dialogsLoading || tagsLoading;
      case "flows":
        return flowsLoading || tagsLoading;
      case "variables":
        return typesLoading || tagsLoading;
      default:
        return tagsLoading;
    }
  };

  const data = getData();
  const isLoading = getLoading();

  // State for wizards
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddWizardOpen, setIsAddWizardOpen] = useState(false);
  const [isCreateWizardOpen, setIsCreateWizardOpen] = useState(false);
  const [selectedBlueprint, setSelectedBlueprint] = useState<GlossaryBlueprint | null>(null);
  const [selectedGlossaryItem, setSelectedGlossaryItem] = useState<GlossaryItem | null>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // Transform data to cards
  const cards = useMemo(() => {
    if (itemType === "library") {
      if (glossaryLoading || blueprintsLoading || tagsLoading) {
        return [];
      }
      return config.transformData(data, tags, getUserNameById, glossaryBlueprints);
    }
    
    if (tagsLoading) {
      return [];
    }
    
    return config.transformData(data, tags, getUserNameById);
  }, [
    data,
    tags,
    config,
    itemType,
    glossaryBlueprints,
    glossaryLoading,
    blueprintsLoading,
    tagsLoading,
    getUserNameById,
    updateTrigger,
  ]);

  // Helper function to extract sections from data
  const extractSectionsFromData = (itemData: any): any[] => {
    if (!itemData) return [];
    if (Array.isArray(itemData)) return itemData;
    if (itemData.sections) {
      return Array.isArray(itemData.sections) 
        ? itemData.sections 
        : Object.values(itemData.sections);
    }
    return [];
  };

  // Extract available sections
  const availableSections: DataViewOption[] = useMemo(() => {
    const sectionsMap = new Map<string, DataViewOption>();
    
    if (itemType === "library" && data && Array.isArray(data)) {
      data.forEach((item: any) => {
        const sections = extractSectionsFromData(item.data);
        sections.forEach((section: any) => {
          if (section?.id && section?.title && !sectionsMap.has(section.id)) {
            sectionsMap.set(section.id, { id: section.id, label: section.title });
          }
        });
      });
    }
    
    if (glossaryBlueprints && glossaryBlueprints.length > 0) {
      glossaryBlueprints.forEach((blueprint: GlossaryBlueprint) => {
        const sections = extractSectionsFromData(blueprint.data);
        sections.forEach((section: any) => {
          if (section?.id && section?.title && !sectionsMap.has(section.id)) {
            sectionsMap.set(section.id, { id: section.id, label: section.title });
          }
        });
      });
    }
    
    return Array.from(sectionsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [itemType, data, glossaryBlueprints]);

  // Extract available fields
  const availableFields: DataViewOption[] = useMemo(() => {
    const fieldsMap = new Map<string, DataViewOption>();
    
    if (itemType === "library" && data && Array.isArray(data)) {
      data.forEach((item: any) => {
        const sections = extractSectionsFromData(item.data);
        sections.forEach((section: any) => {
          if (!section?.fields || !Array.isArray(section.fields)) return;
          section.fields.forEach((field: any) => {
            if (field?.id && field?.label && !fieldsMap.has(field.id)) {
              fieldsMap.set(field.id, { id: field.id, label: field.label, sectionId: section.id });
            }
          });
        });
      });
    }
    
    if (glossaryBlueprints && glossaryBlueprints.length > 0) {
      glossaryBlueprints.forEach((blueprint: GlossaryBlueprint) => {
        const sections = extractSectionsFromData(blueprint.data);
        sections.forEach((section: any) => {
          if (!section?.fields || !Array.isArray(section.fields)) return;
          section.fields.forEach((field: any) => {
            if (field?.id && field?.label && !fieldsMap.has(field.id)) {
              fieldsMap.set(field.id, { id: field.id, label: field.label, sectionId: section.id });
            }
          });
        });
      });
    }
    
    return Array.from(fieldsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [itemType, data, glossaryBlueprints]);

  // Create template lookup
  const templateLookup: Record<string, { id: string; name: string; color?: string }> = useMemo(() => {
    if (!glossaryBlueprints || glossaryBlueprints.length === 0) return {};
    
    const lookup: Record<string, { id: string; name: string; color?: string }> = {};
    glossaryBlueprints.forEach((blueprint: GlossaryBlueprint) => {
      const settings = blueprint.settings || {};
      lookup[blueprint.id] = {
        id: blueprint.id,
        name: blueprint.name,
        color: settings.color as string | undefined,
      };
    });
    
    return lookup;
  }, [glossaryBlueprints]);

  // Page layout configuration
  const [pageLayoutConfig, setPageLayoutConfig] = useState<PageLayoutConfig>({
    title: "",
    spacing: 0,
    padding: 60,
    rows: [
      {
        id: "row-1",
        title: `${config.title} Grid`,
        layoutConfig: {
          elements: [
            {
              id: "grid-1",
              type: "grid-widget",
              props: {
                title: selectedGame ? `${config.title} - ${selectedGame.name}` : config.title,
                cards: cards,
                showHeader: false,
                showSearch: true,
                showTagFilters: true,
                showGridSizeSelector: true,
                availableSections: availableSections,
                availableFields: availableFields,
                rawItems: data,
                templateLookup: templateLookup,
              },
            },
          ],
          gap: 0,
          padding: 0,
        },
      },
    ],
  });

  // Refresh data function - calls the appropriate provider's refresh method based on item type
  const refreshData = async () => {
    if (!selectedGame?.id) return;

    try {
      switch (itemType) {
        case "library":
          await loadGlossaryData();
          break;
        case "blueprints":
          await loadBlueprintsData();
          break;
        case "documents":
          await loadDocumentsData();
          break;
        case "dialogs":
          await loadDialogsData();
          break;
        case "variables":
          await refreshTypes();
          break;
        case "flows":
          await fetchLogicFlows(selectedGame.id, toast);
          break;
        default:
          console.warn(`Refresh not implemented for item type: ${itemType}`);
      }
    } catch (error) {
      console.error(`Error refreshing ${itemType} data:`, error);
      toast({
        title: "Error",
        description: `Failed to refresh ${config.title.toLowerCase()}`,
        variant: "destructive",
      });
    }
  };

  return {
    // Data
    data,
    isLoading,
    cards,
    selectedGame,
    tags,
    glossaryBlueprints: glossaryBlueprints || [],

    // Data view mode support
    availableSections,
    availableFields,
    templateLookup,
    rawItems: data,

    // State
    isEditModalOpen,
    isAddWizardOpen,
    isCreateWizardOpen,
    selectedBlueprint,
    selectedGlossaryItem,
    isEditMode,

    // Setters
    setIsEditModalOpen,
    setIsAddWizardOpen,
    setIsCreateWizardOpen,
    setSelectedBlueprint,
    setSelectedGlossaryItem,
    setIsEditMode,
    setUpdateTrigger,

    // Config
    pageLayoutConfig,
    setPageLayoutConfig,
    config,

    // Providers
    toast,
    confirmation,
    navigate,
    getUserNameById,
    currentPageStructure,
    updateSection,

    // Data provider functions
    deleteDocument,
    createDocument,
    deleteDialogItem,
    createDialogItem,
    updateDialogItem,
    
    // Refresh function
    refreshData,
    
    // Real-time sync
    newItemCount,
    clearNewItems,
  };
}

